import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { useDesignVersionToggle } from '@/hooks'

import Tabs from '../../Tabs/Tabs'
import Icon from '../../Icons'
import { selectBuildingById } from '../../../reducers/buildingSelectorReducer'

import OrganisationPlacesList from './OrganisationPlacesList'
import OrganisationBuilding from './OrganisationBuildingForm'
import OrganisationIntegration from './Integrations/OrganisationIntegration'
import OrganisationPerks from './OrganisationPerks'

// TODO: remove this legacy master/detail page (and its Formik building form) once the new Places design is fully rolled out
const OrganisationPlaces = () => {
  const { t } = useTranslation(['organisation'])
  const navigate = useNavigate()
  const params = useParams()
  const [searchQuery, setSearchQuery] = useState('')
  const selectedBuilding = useSelector(selectBuildingById)(params.buildingId)
  const isActuallyNew =
    typeof selectedBuilding !== 'undefined' && typeof selectedBuilding.id === 'undefined'
  const [activeTab, setActiveTab] = useState(0)

  const {
    canToggle,
    redirect,
    toggle: handleUIToggle,
  } = useDesignVersionToggle({
    group: 'places',
    screen: 'Places',
    // Note: the new design has no `new` page, so only an existing building is opened directly
    to:
      params.buildingId && params.buildingId !== 'new'
        ? {
            params: { buildingId: params.buildingId },
            path: '/dashboard/organisation/places/:buildingId',
          }
        : { path: '/dashboard/organisation/places' },
    version: 'legacy',
  })

  if (redirect) return redirect

  return (
    <div className="columns" style={{ paddingBottom: activeTab === 0 ? '65px' : '0' }}>
      {canToggle && (
        <div className="ctp-organisation__design-toggle">
          <ToggleSwitch
            falsyLabel={t('organisation:places.table.designToggle.old')}
            truthyLabel={t('organisation:places.table.designToggle.new')}
            checked={false}
            onChange={handleUIToggle}
          />
        </div>
      )}
      <div className="column">
        <div className="ctp-short-toolbar-wrapper">
          <button
            className="button button-primary auto-width"
            onClick={() => navigate('/dashboard/organisation/places/legacy/new')}
          >
            {t('organisation:places.list.addNew')}
          </button>
          <div className="ctp-short-toolbar-search">
            <input
              type="text"
              placeholder={t('organisation:places.list.searchPlaceholder')}
              autoComplete="off"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Icon icon="search" />
          </div>
        </div>
        <OrganisationPlacesList searchQuery={searchQuery} params={params} />
      </div>
      <div className="column">
        <div className="ctp-organisation-building-title">
          <h2>
            {isActuallyNew
              ? 'Add New Building'
              : `Edit Building${selectedBuilding.name ? ` — ${selectedBuilding.name}` : ''}`}
          </h2>
        </div>
        <Tabs activeTab={activeTab} onTabChange={(tab) => setActiveTab(tab)}>
          <Tabs.Panel heading={t('organisation:places.tabs.building')}>
            <OrganisationBuilding params={params} />
          </Tabs.Panel>
          <Tabs.Panel heading={t('organisation:places.tabs.neighborhoodPerks')}>
            <OrganisationPerks params={params} />
          </Tabs.Panel>
          <Tabs.Panel heading={t('organisation:places.tabs.integration')}>
            <OrganisationIntegration params={params} />
          </Tabs.Panel>
        </Tabs>
      </div>
    </div>
  )
}

OrganisationPlaces.propTypes = {
  params: PropTypes.object,
}

export default OrganisationPlaces
